import { useRef, useState } from "react";

/* ---------- Placeholder packshot (when no packshot image is provided) ---------- */
function PackshotPlaceholder({ name, accent = "#FF007F" }) {
  return (
    <div className="absolute inset-0 bg-[#f6f3ed] flex flex-col items-center justify-center text-center px-6">
      <span
        aria-hidden="true"
        className="absolute top-4 left-4 inline-block w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: accent }}
      />
      <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-black/40">
        Studio Packshot
      </span>
      <span className="mt-4 font-display italic text-3xl md:text-4xl text-black/80 leading-tight max-w-[14ch]">
        {name}
      </span>
      <span className="mt-6 text-[10px] font-mono uppercase tracking-[0.24em] text-black/40">
        Photo coming soon
      </span>
    </div>
  );
}

/* ---------- One product card ---------- */
function ProductCard({ product, index }) {
  const [liked, setLiked] = useState(false);
  // On touch devices we toggle the active image via tap.
  const [tapShowsModel, setTapShowsModel] = useState(false);

  const gallery = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  const isCarousel = gallery.length > 0;
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const scrollToIndex = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slides = el.querySelectorAll("[data-slide]");
    const target = slides[Math.max(0, Math.min(idx, slides.length - 1))];
    if (!target) return;
    el.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
  };
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const accent = product.accent || "#FF007F";
  const priceStr = product.price != null
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: product.currency || "USD",
        maximumFractionDigits: 0,
      }).format(product.price)
    : null;

  return (
    <article
      data-testid={`product-card-${index}`}
      className="group relative"
    >
      {/* Image stack */}
      <div
        className="relative w-full aspect-[3/4] bg-[#f6f3ed] overflow-hidden cursor-pointer"
        onClick={() => !isCarousel && setTapShowsModel((v) => !v)}
        role={isCarousel ? undefined : "button"}
        tabIndex={isCarousel ? -1 : 0}
        onKeyDown={(e) => {
          if (isCarousel) return;
          if (e.key === "Enter" || e.key === " ") setTapShowsModel((v) => !v);
        }}
        aria-label={isCarousel ? undefined : `${product.name} — tap to see styled photo`}
      >
        {isCarousel ? (
          <>
            <div
              ref={scrollerRef}
              onScroll={onScroll}
              data-testid={`product-carousel-${index}`}
              className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
              style={{ scrollbarWidth: "none" }}
            >
              <style>{`[data-testid="product-carousel-${index}"]::-webkit-scrollbar{display:none}`}</style>
              {gallery.map((src, i) => (
                <div
                  key={i}
                  data-slide
                  className="relative w-full h-full shrink-0 snap-center"
                >
                  <img
                    src={src}
                    alt={`${product.name} — image ${i + 1}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    draggable={false}
                    className="w-full h-full object-cover pointer-events-none"
                  />
                </div>
              ))}
            </div>

            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToIndex(activeIdx - 1);
                  }}
                  aria-label="Previous image"
                  disabled={activeIdx === 0}
                  className="absolute top-1/2 left-3 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm text-black flex items-center justify-center opacity-0 md:group-hover:opacity-100 transition-opacity disabled:opacity-0 hover:bg-white shadow-sm"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToIndex(activeIdx + 1);
                  }}
                  aria-label="Next image"
                  disabled={activeIdx >= gallery.length - 1}
                  className="absolute top-1/2 right-3 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm text-black flex items-center justify-center opacity-0 md:group-hover:opacity-100 transition-opacity disabled:opacity-0 hover:bg-white shadow-sm"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollToIndex(i);
                      }}
                      aria-label={`Go to image ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === activeIdx ? "w-6 bg-black" : "w-1.5 bg-black/30 hover:bg-black/60"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Default — packshot */}
            <div
              className={`absolute inset-0 transition-opacity duration-700 ease-out
                ${tapShowsModel ? "opacity-0 md:opacity-100" : "opacity-100"}
                md:group-hover:opacity-0`}
            >
          {product.packshot ? (
            <img
              src={product.packshot}
              alt={`${product.name} — packshot`}
              loading="lazy"
              className="w-full h-full object-cover"
              style={product.packshotPosition ? { objectPosition: product.packshotPosition } : undefined}
            />
          ) : (
            <PackshotPlaceholder name={product.name} accent={accent} />
          )}
        </div>

        {/* Hover / tapped — styled on-model */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ease-out
            ${tapShowsModel ? "opacity-100 md:opacity-0" : "opacity-0"}
            md:group-hover:opacity-100`}
        >
          {product.model ? (
            <img
              src={product.model}
              alt={`${product.name} — on model`}
              loading="lazy"
              className="w-full h-full object-cover"
              style={product.modelPosition ? { objectPosition: product.modelPosition } : undefined}
            />
          ) : (
            <div className="absolute inset-0 bg-[#efece4] flex flex-col items-center justify-center text-center px-6">
              <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-black/40">
                Styled Photo
              </span>
              <span className="mt-4 font-display italic text-2xl md:text-3xl text-black/60 leading-tight max-w-[14ch]">
                {product.name}
              </span>
              <span className="mt-6 text-[10px] font-mono uppercase tracking-[0.24em] text-black/40">
                On-model shot soon
              </span>
            </div>
          )}
        </div>
          </>
        )}

        {/* Wishlist */}
        <button
          type="button"
          aria-pressed={liked}
          onClick={(e) => {
            e.stopPropagation();
            setLiked((v) => !v);
          }}
          data-testid={`product-wishlist-${index}`}
          className={`absolute top-3 right-3 md:top-4 md:right-4 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full transition-all
            ${liked
              ? "bg-white text-[#E11D74] shadow-md"
              : "bg-white/70 text-black/70 hover:bg-white hover:text-[#E11D74] backdrop-blur-sm"
            }`}
          aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={liked ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>

        {/* Subtle image indicator (mobile) */}
        {!isCarousel && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 md:hidden">
            <span className={`w-1.5 h-1.5 rounded-full ${tapShowsModel ? "bg-black/30" : "bg-black"}`} />
            <span className={`w-1.5 h-1.5 rounded-full ${tapShowsModel ? "bg-black" : "bg-black/30"}`} />
          </div>
        )}
      </div>

      {/* Text block */}
      <div className="mt-5 md:mt-6 pr-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
          {product.brand}
        </p>
        <h3 className="mt-2 text-sm md:text-base text-black/90 leading-snug">
          {product.name}
        </h3>
        {priceStr && (
          <p className="mt-2 text-sm text-black/80 tabular-nums">{priceStr}</p>
        )}
      </div>
    </article>
  );
}

/* ---------- Section ---------- */
export default function ProjectShop({ products = [], title = "Shop the collection", subtitle }) {
  if (!products.length) return null;

  return (
    <section
      data-testid="project-shop"
      className="bg-[#fbf9f5] text-black border-t border-b border-black/5 px-6 md:px-12 lg:px-20 py-24 md:py-32"
    >
      {/* Header */}
      <header className="max-w-7xl mx-auto mb-12 md:mb-20">
        <div className="flex items-end justify-between gap-10 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-black/50">
              The Capsule · Available now
            </p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight text-black">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-5 max-w-md text-sm md:text-base text-black/60 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.28em] text-black/45">
            <span>{products.length} pieces</span>
            <span className="w-px h-3 bg-black/20" />
            <span>Atelier · Limited</span>
            <span className="w-px h-3 bg-black/20" />
            <span>Worldwide shipping</span>
          </div>
        </div>
      </header>

      {/* Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-x-6 md:gap-x-10 gap-y-14 md:gap-y-20">
        {products.map((p, i) => (
          <ProductCard key={p.id || i} product={p} index={i} />
        ))}
      </div>

      {/* Footer note */}
      <p className="max-w-7xl mx-auto mt-16 md:mt-24 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-black/40">
        Made-to-order · Allow 4–6 weeks · Email{" "}
        <a href="mailto:chob923@newschool.edu" className="text-black/70 underline-offset-4 hover:underline">
          chob923@newschool.edu
        </a>{" "}
        for sizing & swatches
      </p>
    </section>
  );
}
